import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { User, Mail, Clock, AlertCircle, Paperclip, FileText, Settings, Headset } from 'lucide-react';
import { MessageWithStatus } from '../../../../../Api/sav-v2/savV2MessagesApi';
import './message-styles.css';

interface MessageItemProps {
  message: MessageWithStatus;
  isCurrentUser: boolean;
  onAttachmentClick?: (attachmentId: number) => void;
  isBeneficiaryView?: boolean;
}

export default function MessageItem({ message, isCurrentUser, onAttachmentClick, isBeneficiaryView = false }: MessageItemProps) {
  const formatDate = (date: string | Date) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return format(dateObj, "d MMMM yyyy 'à' HH:mm", { locale: fr });
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    if (!firstName && !lastName) return '??';
    const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`;
    return initials.toUpperCase() || '??';
  };

  // Déterminer le type de message basé sur senderType
  const isSystemMessage = message.senderType === 'system';
  const isBeneficiaryMessage = message.senderType === 'beneficiary';
  const isGarantMessage = message.senderType === 'garant';

  // Nom de l'expéditeur
  let senderName: string;

  if (isBeneficiaryView) {
    // Vue bénéficiaire : Agent + Système = "Gestionnaire"
    senderName = (message.senderType === 'gestionnaire' || message.senderType === 'system') ? 'Gestionnaire' : 'Vous';
  } else {
    // Vue gestion : afficher le vrai nom selon le senderType
    switch (message.senderType) {
      case 'system':
        senderName = 'Système';
        break;
      case 'beneficiary':
        senderName = 'Bénéficiaire';
        break;
      case 'garant':
        senderName = message.user
          ? `${message.user.firstName || ''} ${message.user.lastName || ''}`.trim() || 'Garant'
          : 'Garant';
        break;
      case 'gestionnaire':
      default:
        senderName = message.user
          ? `${message.user.firstName || ''} ${message.user.lastName || ''}`.trim() || 'Agent'
          : 'Agent';
        break;
    }
  }

  // Styles conditionnels basés sur le statut d'envoi
  const containerClasses = `tw-border-b tw-border-gray-200 tw-px-4 tw-py-4 tw-mb-4 tw-rounded-lg tw-transition-opacity ${
    isCurrentUser ? 'tw-bg-blue-50/30' : 'tw-bg-white'
  } ${
    message.sending ? 'tw-opacity-60' : ''
  } ${
    message.error ? 'tw-bg-red-50 tw-border-red-200' : ''
  }`;

  return (
    <div className={containerClasses}>
      {/* Header du message */}
      <div className="tw-flex tw-items-start tw-gap-3 tw-mb-3">
        {/* Avatar */}
        <div className={`tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-white tw-font-medium tw-text-sm ${
          isBeneficiaryView
            ? (isBeneficiaryMessage ? 'tw-bg-purple-500' : 'tw-bg-blue-600')
            : (isSystemMessage ? 'tw-bg-gray-500' : isBeneficiaryMessage ? 'tw-bg-purple-500' : isGarantMessage ? 'tw-bg-green-600' : 'tw-bg-blue-600')
        }`}>
          {isBeneficiaryView ? (
            isBeneficiaryMessage ? <User size={20} /> : <Headset size={20} />
          ) : (
            isSystemMessage ? <Settings size={20} /> : isBeneficiaryMessage ? <User size={20} /> : isGarantMessage ? getInitials(message.user?.firstName, message.user?.lastName) : getInitials(message.user?.firstName, message.user?.lastName)
          )}
        </div>

        {/* Infos expéditeur */}
        <div className="tw-flex-1">
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-font-semibold tw-text-gray-900">
              {senderName}
            </span>
            {!isBeneficiaryView && isSystemMessage && (
              <span className="tw-px-2 tw-py-0.5 tw-bg-gray-100 tw-text-gray-700 tw-text-xs tw-rounded-full tw-font-medium">
                Automatique
              </span>
            )}
            {!isBeneficiaryView && isBeneficiaryMessage && (
              <span className="tw-px-2 tw-py-0.5 tw-bg-purple-100 tw-text-purple-700 tw-text-xs tw-rounded-full tw-font-medium">
                Bénéficiaire
              </span>
            )}
            {!isBeneficiaryView && isGarantMessage && (
              <span className="tw-px-2 tw-py-0.5 tw-bg-green-100 tw-text-green-700 tw-text-xs tw-rounded-full tw-font-medium">
                Garant
              </span>
            )}
          </div>
          <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-500">
            {message.user?.email && (
              <>
                <Mail size={14} />
                <span>{message.user.email}</span>
                <span className="tw-mx-1">•</span>
              </>
            )}
            {message.sending ? (
              <div className="tw-flex tw-items-center tw-gap-1 tw-text-blue-600">
                <Clock size={14} className="tw-animate-pulse" />
                <span>Envoi en cours...</span>
              </div>
            ) : message.error ? (
              <div className="tw-flex tw-items-center tw-gap-1 tw-text-red-600">
                <AlertCircle size={14} />
                <span>Erreur d'envoi</span>
              </div>
            ) : (
              <span>{formatDate(message.dateCreation)}</span>
            )}
          </div>
        </div>

      </div>

      {/* Corps du message */}
      <div className="tw-pl-13">
        {/* Afficher le sujet s'il existe */}
        {message.content.subject && (
          <div className="tw-mb-2">
            <span className="tw-font-semibold tw-text-gray-900">
              Objet : {message.content.subject}
            </span>
          </div>
        )}

        <div className="tw-prose tw-prose-sm tw-max-w-none tw-text-gray-700">
          {/* Si on a du HTML, on l'affiche, sinon on affiche le texte brut */}
          {message.content.html ? (
            <div
              dangerouslySetInnerHTML={{ __html: message.content.html }}
              className="message-content"
            />
          ) : (
            <pre className="tw-whitespace-pre-wrap tw-font-sans tw-bg-transparent tw-p-0 tw-m-0">
              {message.content.text}
            </pre>
          )}
        </div>

        {/* Afficher les pièces jointes si présentes */}
        {message.content.attachmentsIds && message.content.attachmentsIds.length > 0 && (
          <div className="tw-mt-4">
            <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-600 tw-mb-2">
              <Paperclip size={14} />
              <span>Pièces jointes ({message.content.attachmentsIds.length})</span>
            </div>
            <div className="tw-flex tw-flex-wrap tw-gap-2">
              {message.content.attachmentsIds.map((attachmentId) => (
                <button
                  key={attachmentId}
                  onClick={() => onAttachmentClick?.(attachmentId)}
                  className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-bg-gray-100 tw-rounded-lg tw-text-sm tw-text-gray-700 hover:tw-bg-gray-200 tw-cursor-pointer tw-transition-colors"
                  title={`Cliquer pour ouvrir le document #${attachmentId}`}
                  type="button"
                >
                  <FileText size={16} />
                  <span>Document #{attachmentId}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}